// gydFacilitationService — capture for the Attune Facilitator mode.
//
// Writes one row per (enrollment_id, slide_id) into gyd_facilitations, upserted
// in place as the session progresses (transcript grows; wrap-up + turn_count
// land at the end). Best-effort + behind the fk_attune_capture kill-switch, like
// the learning_interactions dual-write — capture must never break the lesson.
//
// RLS-safe: the learner writes their own row (enrollment ownership); tenant_id is
// resolved from the enrollment, never trusted from the client.

import { supabase } from '../lib/supabase';

/** Tenant flag cache — checked once per session unless explicitly busted. */
let _flagCache: { tenantId: string; enabled: boolean } | null = null;

export function bustAttuneCaptureFlagCache(): void {
  _flagCache = null;
}

/**
 * Reads tenants.feature_flags.fk_attune_capture for the current user's tenant
 * (DB-truth only — deliberately skips the getFeatures cascade so it stays an
 * instant kill switch). Returns false on any error; capture is best-effort.
 */
export async function isAttuneCaptureEnabled(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: userRow } = await supabase
      .from('users')
      .select('tenant_id')
      .eq('id', user.id)
      .maybeSingle();
    if (!userRow?.tenant_id) return false;

    if (_flagCache?.tenantId === userRow.tenant_id) return _flagCache.enabled;

    const { data: tenantRow } = await supabase
      .from('tenants')
      .select('feature_flags')
      .eq('id', userRow.tenant_id)
      .maybeSingle();

    const enabled = Boolean(
      (tenantRow?.feature_flags as Record<string, unknown> | null)?.fk_attune_capture,
    );
    _flagCache = { tenantId: userRow.tenant_id, enabled };
    return enabled;
  } catch {
    return false;
  }
}

export interface FacilitationTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface RecordFacilitationInput {
  /** The enrollment (and tenant) is resolved internally from courseId + the
   *  current user, mirroring activityService — the viewer never threads it. */
  courseId: string;
  lessonId: string;
  slideId: string;
  /** Slide position in the lesson (which facilitator touchpoint this is). */
  touchpointPosition?: number | null;
  transcript: FacilitationTurn[];
  /** The closing insight, set when the session wraps. */
  wrapupInsight?: string | null;
  turnCount: number;
}

export interface RecordFacilitationResult {
  written: boolean;
  skipReason?: string;
}

/**
 * Upsert the facilitation capture row for (enrollment, slide). Call repeatedly
 * as the session progresses — latest state wins. No-op (written:false) when the
 * kill-switch is off or the enrollment can't be resolved.
 */
export async function recordFacilitation(
  input: RecordFacilitationInput,
): Promise<RecordFacilitationResult> {
  if (!(await isAttuneCaptureEnabled())) return { written: false, skipReason: 'flag-off' };

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { written: false, skipReason: 'not-authenticated' };

  const { data: enrollmentRow, error: enrollmentErr } = await supabase
    .from('enrollments')
    .select('id, tenant_id')
    .eq('user_id', user.id)
    .eq('course_id', input.courseId)
    .maybeSingle();
  if (enrollmentErr || !enrollmentRow?.id || !enrollmentRow.tenant_id) {
    return { written: false, skipReason: 'enrollment-not-found' };
  }

  const row = {
    tenant_id: enrollmentRow.tenant_id,
    enrollment_id: enrollmentRow.id,
    lesson_id: input.lessonId,
    slide_id: input.slideId,
    touchpoint_position: input.touchpointPosition ?? null,
    transcript: input.transcript,
    wrapup_insight: input.wrapupInsight ?? null,
    turn_count: input.turnCount,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('gyd_facilitations')
    .upsert(row, { onConflict: 'enrollment_id,slide_id' });
  if (error) return { written: false, skipReason: `upsert-error:${error.code}` };

  return { written: true };
}

export interface FacilitationRecord {
  id: string;
  enrollment_id: string;
  lesson_id: string;
  slide_id: string;
  touchpoint_position: number | null;
  transcript: FacilitationTurn[];
  wrapup_insight: string | null;
  turn_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Admin read: facilitation records for the caller's tenant (RLS-scoped), most
 * recent first. Returns [] on error (not-found is not an error).
 */
export async function listFacilitations(limit = 200): Promise<FacilitationRecord[]> {
  const { data, error } = await supabase
    .from('gyd_facilitations')
    .select('id, enrollment_id, lesson_id, slide_id, touchpoint_position, transcript, wrapup_insight, turn_count, created_at, updated_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as FacilitationRecord[];
}
